import React, { useEffect } from "react";
import MainLayout from "../../Layout/mainLayout";
import "./ViewDeal.css";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { viewDeal } from "../../Redux/Actions/dealsAction";

const ViewDeal = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { deal, loading, error } = useSelector((state) => state.deals || {});
  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

  useEffect(() => {
    if (id) dispatch(viewDeal(id));
  }, [dispatch, id]);

  const data = deal?.data;

  if (loading)
    return (
      <MainLayout>
        <div className="vd-state-wrapper">
          <div className="vd-spinner" />
          <p className="vd-state-text">Loading deal...</p>
        </div>
      </MainLayout>
    );

  if (error)
    return (
      <MainLayout>
        <div className="vd-state-wrapper">
          <div className="vd-state-icon vd-state-icon--error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p className="vd-state-text">Error: {error}</p>
        </div>
      </MainLayout>
    );

  if (!data)
    return (
      <MainLayout>
        <div className="vd-state-wrapper">
          <div className="vd-state-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <p className="vd-state-text">No deal data available</p>
        </div>
      </MainLayout>
    );

  return (
    <MainLayout>
      <div className="vd-wrappers">

        {/* ── Header ── */}
        <div className="vd-header">
          <div className="vd-header-left">
            <div className="vd-header-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <div>
              <h1 className="vd-title">Deal Details</h1>
              <p className="vd-subtitle">Viewing full information for this deal</p>
            </div>
          </div>
          <button className="vd-back-btn" type="button" onClick={() => navigate("/dealsAndOffers")}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to List
          </button>
        </div>

        {/* ── Body ── */}
        <div className="vd-body">

          {/* ── Left: Image + Status ── */}
          <div className="vd-left">
            <div className="vd-image-card">
              {data.image ? (
                <img src={data.image} alt={data.name} className="vd-image" />
              ) : (
                <div className="vd-image-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>No Image</span>
                </div>
              )}
            </div>

            {/* Quick Info */}
            <div className="vd-quick-card">
              <div className="vd-quick-row">
                <span className="vd-quick-label">Deal Name</span>
                <span className="vd-quick-value">{data.name || "—"}</span>
              </div>
              <div className="vd-divider" />
              <div className="vd-quick-row">
                <span className="vd-quick-label">Status</span>
                <span className={`vd-badge vd-badge--${(data.status || "").toLowerCase()}`}>
                  {data.status || "—"}
                </span>
              </div>
              <div className="vd-divider" />
              <div className="vd-quick-row">
                <span className="vd-quick-label">Is Special</span>
                <span className={`vd-badge ${data.is_special ? "vd-badge--special" : "vd-badge--no"}`}>
                  {data.is_special ? "Yes" : "No"}
                </span>
              </div>
              <div className="vd-divider" />
              <div className="vd-quick-row">
                <span className="vd-quick-label">Deal Type</span>
                <span className="vd-quick-value vd-capitalize">{data.deal_type || data.type || "—"}</span>
              </div>
            </div>
          </div>

          {/* ── Right: Detail Cards ── */}
          <div className="vd-right">

            {/* Card 1: Discount Info */}
            <div className="vd-card">
              <div className="vd-card-header">
                <span className="vd-card-badge">01</span>
                <h2 className="vd-card-title">Discount Information</h2>
              </div>
              <div className="vd-grid vd-grid-2">
                <div className="vd-info-item">
                  <span className="vd-info-label">Discount Type</span>
                  <span className="vd-info-value">{data.discount_type || "—"}</span>
                </div>
                <div className="vd-info-item">
                  <span className="vd-info-label">Discount Value</span>
                  <span className="vd-info-value vd-highlight">
                    ₹{data.value ? Number(data.value).toLocaleString() : "—"}
                  </span>
                </div>
                <div className="vd-info-item">
                  <span className="vd-info-label">Start Date</span>
                  <span className="vd-info-value">
                    {data.start_date ? new Date(data.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </span>
                </div>
                <div className="vd-info-item">
                  <span className="vd-info-label">End Date</span>
                  <span className="vd-info-value">
                    {data.end_date ? new Date(data.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                  </span>
                </div>
                {data.term_and_condition && (
                  <div className="vd-info-item vd-info-item--full">
                    <span className="vd-info-label">Terms &amp; Condition</span>
                    <span className="vd-info-value">{data.term_and_condition}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Card 2: Builder Info */}
            {data.builder && (
              <div className="vd-card">
                <div className="vd-card-header">
                  <span className="vd-card-badge">02</span>
                  <h2 className="vd-card-title">Builder Information</h2>
                </div>
                <div className="vd-grid vd-grid-2">
                  <div className="vd-info-item">
                    <span className="vd-info-label">Brand Name</span>
                    <span className="vd-info-value">{data.builder?.brand_name || "—"}</span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">Organization</span>
                    <span className="vd-info-value">{data.builder?.organization_name || "—"}</span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">RERA ID</span>
                    <span className="vd-info-value vd-mono">{data.builder?.rera_id || "—"}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Card 3: Project Info */}
            {data.project && (
              <div className="vd-card">
                <div className="vd-card-header">
                  <span className="vd-card-badge">03</span>
                  <h2 className="vd-card-title">Project Information</h2>
                </div>
                <div className="vd-grid vd-grid-2">
                  <div className="vd-info-item">
                    <span className="vd-info-label">Project Name</span>
                    <span className="vd-info-value">{data.project?.project_name || "—"}</span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">Brand</span>
                    <span className="vd-info-value">{data.project?.brand_name || "—"}</span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">Project ID</span>
                    <span className="vd-info-value vd-mono">{data.project?.project_id || "—"}</span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">RERA ID</span>
                    <span className="vd-info-value vd-mono">{data.project?.rera_id || "—"}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Card 4: Location */}
            {(data.country || data.state || data.city) && (
              <div className="vd-card">
                <div className="vd-card-header">
                  <span className="vd-card-badge">04</span>
                  <h2 className="vd-card-title">Location</h2>
                </div>
                <div className="vd-grid vd-grid-3">
                  <div className="vd-info-item">
                    <span className="vd-info-label">Country</span>
                    <span className="vd-info-value">
                      {typeof data.country === "object"
                        ? data.country?.country_name || "—"
                        : data.country || "—"}
                    </span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">State</span>
                    <span className="vd-info-value">
                      {typeof data.state === "object"
                        ? data.state?.state_name || "—"
                        : data.state || "—"}
                    </span>
                  </div>
                  <div className="vd-info-item">
                    <span className="vd-info-label">City</span>
                    <span className="vd-info-value">
                      {typeof data.city === "object"
                        ? data.city?.city_name || "—"
                        : data.city || "—"}
                    </span>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* ── Footer Actions ── */}
        {canEdit &&
          <div className="vd-footer">
            <button type="button" className="vd-btn vd-btn--cancel" onClick={() => navigate("/dealsAndOffers")}>
              Cancel
            </button>
            <button type="button" className="vd-btn vd-btn--edit" onClick={() => navigate(`/dealsAndOffers/edit/${id}`)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit Deal
            </button>
          </div>
        }

      </div>
    </MainLayout >
  );
};

export default ViewDeal;